import React, { useCallback, useEffect, useState } from 'react';
import { DebugContext } from 'App';
import { isMobile } from 'react-device-detect';
import {
	NavigationWrapper,
	SessionWrapper,
	TargetsAllianzUtilsWrapper,
	UiWrapper,
} from '@coinscrap/webapp-core';
import { MainModal } from 'components/MainModal/MainModal';
import { useGetError } from 'hooks/useGetError';

export const useProfession = (
	simulationStep,
	prevQuestions,
	targetId,
	professionCode,
	trigger,
	setTrigger,
	customOnError = undefined,
	agent,
) => {
	const { openModal } = UiWrapper.use();
	const { navigateTo } = NavigationWrapper.use();
	const { clearSession } = SessionWrapper.use();
	const isDebug = React.useContext(DebugContext);
	const { getProfessionQuestions } = TargetsAllianzUtilsWrapper.use();
	const getError = useGetError();

	const onError = useCallback(
		(e) => {
			openModal(() => <MainModal type="error" text={getError(e)} />, {
				disableClickAway: true,
				closeButton: true,
				modalClass: isMobile ? 'modal-xl' : 'modal-main',
				key: 'modal-useProfession-KO',
			});
		},
		[getError, openModal],
	);

	const [startProcess, setStartProcess] = useState(false);

	const [professionQuestions, setProfessionQuestions] = useState(prevQuestions || []);
	const [professionWarning, setProfessionWarning] = useState(undefined);
	const [loadingQuestions, setLoadingQuestions] = useState(null);

	useEffect(() => {
		if (simulationStep === 1) {
			setStartProcess(true);
		}
		return () => {};
	}, [simulationStep]);

	const getProfessionQuest = useCallback(
		async (professionCode) => {
			if (!professionCode) return;
			try {
				setLoadingQuestions('Obteniendo preguntas');

				let professionQuestions;
				try {
					professionQuestions = await getProfessionQuestions(targetId, professionCode);
				} catch (e) {
					console.log('❌ ERROR', e);
					setProfessionQuestions([]);
					if (customOnError) {
						customOnError(
							e,
							setTrigger,
							openModal,
							navigateTo,
							isDebug,
							clearSession,
							agent,
						);
					} else {
						onError(e);
					}
					return;
				}

				console.log('✅ getProfessionQuestions', professionQuestions);

				if (
					professionQuestions?.output?.operationData?.profesionQuestionList?.questionItem
				) {
					// PROFESSION QUESTIONS
					setProfessionQuestions(
						professionQuestions?.output?.operationData?.profesionQuestionList
							?.questionItem,
					);
					setProfessionWarning(undefined);
				} else {
					setProfessionQuestions([]);
					setProfessionWarning(
						professionQuestions?.output?.operationWarningList?.warning,
					);
				}
			} catch (e) {
				if (customOnError) {
					customOnError(e, setTrigger, openModal, navigateTo, isDebug, clearSession);
				} else {
					onError(e);
				}
			} finally {
				setLoadingQuestions(undefined);
			}
		},
		[
			clearSession,
			customOnError,
			getProfessionQuestions,
			isDebug,
			navigateTo,
			onError,
			openModal,
			setTrigger,
			targetId,
		],
	);

	useEffect(() => {
		if (startProcess && professionCode) {
			getProfessionQuest(professionCode);
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startProcess, professionCode, trigger]);

	return { loadingQuestions, professionQuestions, professionWarning };
};
